import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const DebugContext = createContext(null);

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};

export const DebugProvider = ({ children }) => {
  // Initialize from localStorage to persist across reloads
  const [isDebugEnabled, setIsDebugEnabled] = useState(() => {
    return localStorage.getItem('math_track_debug_mode') === 'true';
  });
  
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    localStorage.setItem('math_track_debug_mode', isDebugEnabled);
  }, [isDebugEnabled]);

  const addLog = (type, message, data = null) => {
    if (!isDebugEnabled) return;
    
    const newLog = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      type, // 'info', 'error', 'api', 'auth'
      message,
      data
    };
    
    setLogs(prev => [newLog, ...prev].slice(0, 50)); // Keep last 50 logs
    
    // Also log to console
    console.log(`[DEBUG:${type}] ${message}`, data || '');
  };

  const clearLogs = () => setLogs([]);

  const toggleDebug = () => {
    setIsDebugEnabled(prev => {
      const newVal = !prev;
      if (newVal) toast.info('Debug Mode Enabled');
      else toast.info('Debug Mode Disabled');
      return newVal;
    });
  };

  return (
    <DebugContext.Provider value={{ 
      isDebugEnabled, 
      toggleDebug, 
      logs, 
      addLog, 
      clearLogs 
    }}>
      {children}
      {isDebugEnabled && <DebugOverlay logs={logs} clearLogs={clearLogs} toggleDebug={toggleDebug} />}
    </DebugContext.Provider>
  );
};

// Debug UI Component
const DebugOverlay = ({ logs, clearLogs, toggleDebug }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isExpanded) {
    return (
      <button 
        onClick={() => setIsExpanded(true)}
        className="fixed bottom-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-mono shadow-lg z-50 hover:bg-red-700 transition-colors"
      >
        🐞 Debug
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 h-96 bg-black/90 text-green-400 font-mono text-xs rounded-lg shadow-2xl z-50 flex flex-col border border-green-500/30 backdrop-blur-sm">
      <div className="flex justify-between items-center p-2 border-b border-white/10 bg-white/5">
        <span className="font-bold">Debug Console</span>
        <div className="flex gap-2">
          <button onClick={clearLogs} className="hover:text-white px-2">Clear</button>
          <button onClick={() => setIsExpanded(false)} className="hover:text-white px-2">_</button>
          <button onClick={toggleDebug} className="hover:text-red-400 px-2">Close</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-2">
        {logs.length === 0 ? (
          <div className="text-gray-500 italic text-center mt-10">No logs yet...</div>
        ) : (
          logs.map(log => (
            <div key={log.id} className="border-b border-white/5 pb-1">
              <div className="flex justify-between text-[10px] text-gray-500">
                <span>{log.timestamp.split('T')[1].split('.')[0]}</span>
                <span className={`uppercase font-bold ${
                  log.type === 'error' ? 'text-red-500' : 
                  log.type === 'auth' ? 'text-yellow-500' : 
                  log.type === 'api' ? 'text-blue-400' : 'text-gray-400'
                }`}>{log.type}</span>
              </div>
              <div className="font-semibold">{log.message}</div>
              {log.data && (
                <pre className="mt-1 bg-white/5 p-1 rounded overflow-x-auto text-[10px] text-gray-300">
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
